import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Users,
  Link as LinkIcon,
  MapPin,
  CreditCard,
  Trophy,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  UserCircle,
  SlidersHorizontal,
  Building2,
  ArrowLeftRight,
  ShieldCheck,
  History,
  Megaphone,
  Wallet,
  ChevronDown,
  ChevronRight,
  Cog,
  PiggyBank,
  Receipt,
  LineChart,
  ClipboardList,
  Target,
  Handshake,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { PaywallBanner } from './PaywallBanner';

// Module structure: each module has a label, icon, and children.
const MODULES = [
  {
    id: 'geral',
    type: 'item',
    name: 'Dashboard Geral',
    href: '/dashboard',
    icon: LayoutDashboard,
    section: null,
  },
  {
    id: 'insights',
    type: 'item',
    name: 'Insights',
    href: '/insights',
    icon: Sparkles,
    section: null,
    requiresInsights: true,
  },
  {
    id: 'mobilizacao',
    type: 'group',
    name: 'Mobilização',
    icon: Megaphone,
    children: [
      { name: 'Dashboard',  href: '/mobilizacao/dashboard', icon: LayoutDashboard, section: null },
      { name: 'Dobradas',   href: '/mobilizacao/dobradas',  icon: Handshake,       section: 'dobradas' },
      { name: 'Lideranças', href: '/liderancas',            icon: Users,           section: 'liderancas' },
      { name: 'Apoiadores', href: '/apoiadores',            icon: UserCircle,      section: 'apoiadores' },
      { name: 'Links',      href: '/links',                 icon: LinkIcon,        section: 'links' },
      { name: 'Destinos',   href: '/destinos',              icon: MapPin,          section: 'destinos' },
      { name: 'Cartões',    href: '/cartoes',               icon: CreditCard,      section: 'cartoes' },
      { name: 'Ranking',    href: '/ranking',               icon: Trophy,          section: 'ranking' },
      { name: 'Metas',      href: '/metas',                 icon: Target,          section: 'liderancas' },
      { name: 'Relatórios', href: '/relatorios',            icon: FileText,        section: 'relatorios' },
    ],
  },
  {
    id: 'financeiro',
    type: 'group',
    name: 'Financeiro',
    icon: Wallet,
    children: [
      { name: 'Dashboard',          href: '/financeiro/dashboard',   icon: LayoutDashboard, section: 'financeiro_analise' },
      { name: 'Planejamento',       href: '/financeiro/planejamento', icon: PiggyBank,       section: 'financeiro_planejamento' },
      { name: 'Gastos',             href: '/financeiro/gastos',       icon: Receipt,         section: 'financeiro_gastos' },
      { name: 'Análise',            href: '/financeiro/analise',      icon: LineChart,       section: 'financeiro_analise' },
      { name: 'Prestação de Contas', href: '/financeiro/prestacao',   icon: ClipboardList,   section: 'financeiro_prestacao' },
    ],
  },
  {
    id: 'configuracoes',
    type: 'group',
    name: 'Configurações',
    icon: Cog,
    children: [
      { name: 'Configurações', href: '/configuracoes', icon: SlidersHorizontal, section: 'configuracoes' },
      { name: 'Logs',          href: '/logs',          icon: History,           section: 'logs' },
    ],
  },
  {
    id: 'usuarios',
    type: 'item',
    name: 'Usuários',
    href: '/usuarios',
    icon: Settings,
    section: 'usuarios',
  },
];

const COLLAPSED_KEY = 'meadd:nav:collapsed';

export const Layout = ({ children }) => {
  const { user, logout, exitTenant, isSuperAdmin, isImpersonating, activeTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Track which collapsible groups are open
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_e) {
      // ignore
    }
    return { mobilizacao: true, financeiro: false, configuracoes: false };
  });

  // Auto-open the group containing the active route on first load / route change
  useEffect(() => {
    const activeGroup = MODULES.find(
      (m) => m.type === 'group' && m.children.some((c) => location.pathname.startsWith(c.href))
    );
    if (activeGroup && !openGroups[activeGroup.id]) {
      setOpenGroups((prev) => ({ ...prev, [activeGroup.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Persist collapsed state
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify(openGroups));
    } catch (_e) {
      // ignore
    }
  }, [openGroups]);

  const hasAccess = (section) => {
    if (!section) return true;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    const level = user?.permissions?.[section] || 'none';
    return level !== 'none';
  };

  // Build visible navigation: hide groups whose all children are inaccessible
  const visibleModules = useMemo(() => {
    return MODULES.map((m) => {
      if (m.type === 'item') {
        if (m.requiresInsights && !activeTenant?.insights_enabled) return null;
        return hasAccess(m.section) ? m : null;
      }
      const allowedChildren = m.children.filter((c) => hasAccess(c.section));
      if (allowedChildren.length === 0) return null;
      return { ...m, children: allowedChildren };
    }).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTenant]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleExitTenant = async () => {
    const res = await exitTenant();
    if (res.success) {
      toast.success('Saindo do cliente. Voltando ao painel da plataforma.');
      navigate('/platform');
    }
  };

  const tenantInitial = activeTenant?.name ? activeTenant.name.charAt(0).toUpperCase() : 'T';
  const tenantColor = activeTenant?.primary_color || '#1e3a8a';
  const tenantLogo = activeTenant?.logo_url;
  const tenantFooter = activeTenant?.footer_text;

  const isItemActive = (href) => location.pathname === href;

  const renderItem = (item, depth = 0) => {
    const active = isItemActive(item.href);
    return (
      <Link
        key={item.href}
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          depth > 0 ? 'pl-9' : ''
        } ${
          active
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-muted'
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        <item.icon className={`h-4 w-4 mr-2.5 ${depth === 0 ? 'h-5 w-5 mr-3' : ''}`} />
        <span className="truncate">{item.name}</span>
      </Link>
    );
  };

  const renderGroup = (module) => {
    const open = !!openGroups[module.id];
    const hasActiveChild = module.children.some((c) => isItemActive(c.href));
    const ChevronIcon = open ? ChevronDown : ChevronRight;
    return (
      <div key={module.id} className="space-y-0.5">
        <button
          type="button"
          onClick={() => toggleGroup(module.id)}
          data-testid={`nav-group-${module.id}`}
          className={`w-full flex items-center px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-lg transition-colors ${
            hasActiveChild
              ? 'text-primary bg-primary/5'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <module.icon className="h-4 w-4 mr-2.5" />
          <span className="flex-1 text-left">{module.name}</span>
          <ChevronIcon className="h-4 w-4 shrink-0" />
        </button>
        {open && (
          <div className="space-y-0.5 mt-0.5">
            {module.children.map((c) => renderItem(c, 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* MeAdd logo header */}
          <div className="flex items-center justify-between h-24 px-4 border-b border-border bg-white">
            <img
              src="/brand/meadd-logo.png"
              alt="MeAdd"
              className="h-[4.25rem] object-contain"
              data-testid="meadd-logo-sidebar"
            />
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-700"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tenant header */}
          {activeTenant && (
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/40 min-w-0">
              {tenantLogo ? (
                <img
                  src={tenantLogo}
                  alt={activeTenant.name}
                  className="h-8 max-w-[140px] object-contain"
                  data-testid="tenant-logo-sidebar"
                />
              ) : (
                <div
                  className="h-8 w-8 rounded-lg flex items-center justify-center font-bold text-white text-sm shrink-0"
                  style={{ background: tenantColor }}
                >
                  {tenantInitial}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground truncate" data-testid="tenant-name-sidebar">
                  {activeTenant.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  /{activeTenant.slug}
                </div>
              </div>
            </div>
          )}

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
            {visibleModules.map((m) =>
              m.type === 'item' ? renderItem(m) : renderGroup(m)
            )}
          </nav>

          <div className="p-4 border-t border-border">
            <div className="flex items-center mb-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <UserCircle className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-xl transition-colors"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <PaywallBanner />
        {isImpersonating && (
          <div
            className="bg-indigo-600 text-white px-4 py-2 text-sm flex items-center justify-between gap-3 flex-wrap"
            data-testid="impersonation-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              <ShieldCheck className="h-4 w-4 shrink-0" />
              <span className="truncate">
                Você está visualizando <strong>{activeTenant?.name}</strong> como Super Admin.
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExitTenant}
              className="bg-white text-indigo-700 hover:bg-indigo-50"
              data-testid="exit-tenant-btn"
            >
              <ArrowLeftRight className="h-4 w-4 mr-1.5" />
              Sair do cliente
            </Button>
          </div>
        )}

        <header className="sticky top-0 z-30 h-16 bg-card border-b border-border">
          <div className="flex items-center justify-between h-full px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
              data-testid="open-sidebar-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/platform')}
                className="ml-auto"
                data-testid="platform-home-btn"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Painel da plataforma
              </Button>
            )}
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>

        {tenantFooter && (
          <footer
            className="px-4 sm:px-6 lg:px-8 py-4 border-t border-border bg-card text-xs text-muted-foreground text-center"
            data-testid="tenant-footer"
          >
            {tenantFooter}
          </footer>
        )}
      </div>
    </div>
  );
};
